<script lang="ts" setup>
import BaseLabel from '@/atoms/BaseLabel.vue';
import BaseTag, { BaseTagProps } from '@/atoms/BaseTag.vue';
import BaseButton, { BaseButtonProps } from '@/atoms/BaseButton.vue';

export type ActionButtonProps = Partial<BaseButtonProps> &
  Pick<BaseButtonProps, 'iconName'> & { onClick: () => void | Promise<any> };

export type TitleHeaderTab = BaseTagProps & {
  value: string | number | any;
};

export type TitleHeaderProps = {
  classes?: string;
  actionButton?: ActionButtonProps | false;
  secondActionButton?: ActionButtonProps;
  title?: string;
  description?: string;
  descriptionColor?: string | any;
  tag?: BaseTagProps | boolean | null;

  /** NEW */
  tabs?: TitleHeaderTab[];
  activeTab?: string | number;

  scroll?: boolean;
  onBack?: () => void;
};

const props = withDefaults(defineProps<TitleHeaderProps>(), {
  descriptionColor: 'secondary',
});

const emit = defineEmits<{
  (e: 'tab-click', value: TitleHeaderTab['value']): void;
}>();

const onPreviousPageButtonClick = (): void => {
  props.onBack?.();
};
</script>

<template>
  <header :class="classes">
    <!-- MODE SCROLL -->
    <div v-if="scroll" class="flex justify-between items-center border-b border-primary py-3">
      <BaseButton
        v-if="!!props.onBack"
        icon-name="arrow-left"
        size="small"
        type="secondary"
        @click="onPreviousPageButtonClick"
      />

      <BaseLabel :text="title" color="primary" preset="medium" />

      <div class="flex space-x-2">
        <BaseButton
          v-if="actionButton"
          size="small"
          type="secondary"
          v-bind="actionButton"
          @click="actionButton.onClick"
        />
        <BaseButton
          v-if="secondActionButton"
          size="small"
          type="secondary"
          v-bind="secondActionButton"
          @click="secondActionButton.onClick"
        />
      </div>
    </div>

    <!-- MODE NORMAL -->
    <div v-else class="flex flex-col pt-4">
      <div class="flex w-full justify-between">
        <BaseButton
          id="header-back-btn"
          :class="{ invisible: !props.onBack }"
          icon-name="arrow-left"
          type="secondary"
          @click="onPreviousPageButtonClick"
        />

        <div class="flex space-x-2">
          <BaseButton
            v-if="actionButton"
            type="secondary"
            v-bind="actionButton"
            @click="actionButton.onClick"
          />
          <BaseButton
            v-if="secondActionButton"
            type="secondary"
            v-bind="secondActionButton"
            @click="secondActionButton.onClick"
          />
        </div>
      </div>

      <div class="mt-3 flex flex-col">
        <BaseTag v-if="tag" class="mt-3" size="small" type="outline" v-bind="tag" />

        <h1>
          <BaseLabel :text="title" color="primary" preset="h2" />
        </h1>

        <BaseLabel
          v-if="description"
          :color="descriptionColor"
          :text="description"
          class="mt-1"
          preset="medium"
          weight="regular"
        />

        <!-- ✅ TABS -->
        <div v-if="tabs && tabs.length" class="flex space-x-2 mt-3">
          <BaseTag
            v-for="tab in tabs"
            :key="tab.value"
            :type="activeTab === tab.value ? 'accent' : 'outline'"
            clickable
            size="medium"
            v-bind="tab"
            @click="emit('tab-click', tab.value)"
          />
        </div>
      </div>
    </div>
  </header>
</template>
